export type PushSupportState =
  | 'unsupported'
  | 'missing_config'
  | 'denied'
  | 'unsubscribed'
  | 'subscribed';

export interface PushStatus {
  state: PushSupportState;
  permission: NotificationPermission | 'unsupported';
  subscription: PushSubscription | null;
}

export function getPushEndpointBase(): string | null {
  const value = import.meta.env.VITE_PUSH_URL;
  return typeof value === 'string' && value.trim() ? value.trim().replace(/\/+$/, '') : null;
}

export function getPushPublicKey(): string | null {
  const value = import.meta.env.VITE_PUSH_PUBLIC_KEY;
  return typeof value === 'string' && value.trim() ? value.trim() : null;
}

export function supportsWebPush(): boolean {
  return (
    typeof window !== 'undefined' &&
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    typeof Notification !== 'undefined'
  );
}

export function decodeBase64Url(input: string): Uint8Array {
  const normalized = input.replace(/-/g, '+').replace(/_/g, '/');
  const padding = '='.repeat((4 - (normalized.length % 4)) % 4);
  const decoded = window.atob(`${normalized}${padding}`);
  return Uint8Array.from(decoded, (char) => char.charCodeAt(0)) as Uint8Array;
}

export async function readPushStatus(): Promise<PushStatus> {
  if (!supportsWebPush()) {
    return { state: 'unsupported', permission: 'unsupported', subscription: null };
  }

  const permission = Notification.permission;
  if (!getPushEndpointBase() || !getPushPublicKey()) {
    return { state: 'missing_config', permission, subscription: null };
  }
  if (permission === 'denied') {
    return { state: 'denied', permission, subscription: null };
  }

  const registration = await navigator.serviceWorker.ready;
  const subscription = await registration.pushManager.getSubscription();
  return {
    state: subscription ? 'subscribed' : 'unsubscribed',
    permission,
    subscription,
  };
}

async function postSubscription(
  method: 'POST' | 'DELETE',
  subscription: PushSubscription,
): Promise<void> {
  const base = getPushEndpointBase();
  if (!base) throw new Error('Push notifications are not configured.');

  const response = await fetch(`${base}/v1/rooms/default/subscriptions`, {
    method,
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(subscription.toJSON()),
  });

  if (!response.ok) {
    throw new Error(`${method} subscription failed with ${response.status}`);
  }
}

export async function subscribeToPush(): Promise<PushStatus> {
  if (!supportsWebPush()) {
    return { state: 'unsupported', permission: 'unsupported', subscription: null };
  }
  const publicKey = getPushPublicKey();
  if (!publicKey || !getPushEndpointBase()) {
    return {
      state: 'missing_config',
      permission: Notification.permission,
      subscription: null,
    };
  }

  const registration = await navigator.serviceWorker.ready;
  const existing = await registration.pushManager.getSubscription();
  if (existing) {
    await postSubscription('POST', existing);
    return {
      state: 'subscribed',
      permission: Notification.permission,
      subscription: existing,
    };
  }

  const subscription = await registration.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: decodeBase64Url(publicKey) as unknown as BufferSource,
  });

  await postSubscription('POST', subscription);
  return {
    state: 'subscribed',
    permission: Notification.permission,
    subscription,
  };
}

export async function unsubscribeFromPush(): Promise<PushStatus> {
  if (!supportsWebPush()) {
    return { state: 'unsupported', permission: 'unsupported', subscription: null };
  }

  const registration = await navigator.serviceWorker.ready;
  const subscription = await registration.pushManager.getSubscription();
  if (!subscription) {
    return {
      state: getPushEndpointBase() && getPushPublicKey() ? 'unsubscribed' : 'missing_config',
      permission: Notification.permission,
      subscription: null,
    };
  }

  await postSubscription('DELETE', subscription);
  await subscription.unsubscribe();
  return {
    state: getPushEndpointBase() && getPushPublicKey() ? 'unsubscribed' : 'missing_config',
    permission: Notification.permission,
    subscription: null,
  };
}
